/**
 * Developer Inspector Panel
 * Shows real-time sync state, repository events, and metrics
 * Only available in development mode
 */

import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useSyncStatus, useMetrics, useRepositoryLogger } from '@/hooks/repository';
import { useActiveGroup } from '@/stores/group.store';
import { useSyncBackoff } from '@/contexts/sync-backoff.context';
import { X, Activity, Database, Clock, AlertCircle } from 'lucide-react';

export const SyncInspector: React.FC = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedTab, setSelectedTab] = React.useState<'status' | 'metrics' | 'logs'>('status');
  const [, forceUpdate] = React.useReducer(x => x + 1, 0);
  const { activeGroupId } = useActiveGroup();
  const syncStatus = useSyncStatus(activeGroupId || '');
  const { data: metrics } = useMetrics();
  const logger = useRepositoryLogger();
  const backoffState = useSyncBackoff();

  // Force re-render every second to update countdown
  React.useEffect(() => {
    const interval = setInterval(forceUpdate, 1000);
    return () => clearInterval(interval);
  }, []);

  // Calculate backoff info
  const now = Date.now();
  const isInBackoff = backoffState.failureCount > 0 && now < backoffState.nextSyncTime;
  const backoffSecondsRemaining = isInBackoff
    ? Math.ceil((backoffState.nextSyncTime - now) / 1000)
    : 0;

  // Only show in development and on desktop (not mobile)
  if (import.meta.env.PROD) {
    return null;
  }

  if (!isOpen) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 z-50 shadow-lg hidden md:flex"
      >
        <Activity className="h-4 w-4 mr-2" />
        Dev Inspector
      </Button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 w-96 max-h-96 overflow-hidden shadow-2xl hidden md:block">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Repository Inspector
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-2">
            <Button
              variant={selectedTab === 'status' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setSelectedTab('status')}
            >
              <Database className="h-3 w-3 mr-1" />
              Status
            </Button>
            <Button
              variant={selectedTab === 'metrics' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setSelectedTab('metrics')}
            >
              <Activity className="h-3 w-3 mr-1" />
              Metrics
            </Button>
            <Button
              variant={selectedTab === 'logs' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setSelectedTab('logs')}
            >
              <Clock className="h-3 w-3 mr-1" />
              Logs
            </Button>
          </div>
        </CardHeader>

        <CardContent className="text-xs max-h-64 overflow-y-auto">
          {selectedTab === 'status' && (
            <div className="space-y-3">
              <div>
                <div className="font-semibold mb-1">Sync Status</div>
                <div className="flex gap-2 flex-wrap">
                  <Badge variant={syncStatus.isSyncing ? 'default' : 'secondary'}>
                    {syncStatus.isSyncing ? 'Syncing' : 'Idle'}
                  </Badge>
                  <Badge variant="outline">
                    Synced: {syncStatus.synced}
                  </Badge>
                  <Badge variant={syncStatus.pending > 0 ? 'default' : 'outline'}>
                    Pending: {syncStatus.pending}
                  </Badge>
                  <Badge variant={syncStatus.failed > 0 ? 'destructive' : 'outline'}>
                    Failed: {syncStatus.failed}
                  </Badge>
                </div>
              </div>

              {syncStatus.lastSyncAt && (
                <div>
                  <div className="font-semibold mb-1">Last Sync</div>
                  <div className="text-muted-foreground">
                    {new Date(syncStatus.lastSyncAt).toLocaleString('sv-SE', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                    })}
                  </div>
                </div>
              )}

              {isInBackoff && (
                <div>
                  <div className="font-semibold mb-1 text-amber-600 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Waiting for Backend
                  </div>
                  <div className="text-amber-600 bg-amber-50 p-2 rounded text-xs">
                    {backoffState.failureCount} failed attempt{backoffState.failureCount !== 1 ? 's' : ''}.
                    Retrying in {backoffSecondsRemaining}s...
                  </div>
                </div>
              )}

              {syncStatus.error && (
                <div>
                  <div className="font-semibold mb-1 text-red-600">Error</div>
                  <div className="text-red-600 bg-red-50 p-2 rounded text-xs">
                    {syncStatus.error}
                  </div>
                </div>
              )}

              <div>
                <div className="font-semibold mb-1">Group ID</div>
                <div className="text-muted-foreground font-mono text-xs">
                  {activeGroupId || 'No group selected'}
                </div>
              </div>
            </div>
          )}

          {selectedTab === 'metrics' && (
            <div className="space-y-2">
              {metrics && metrics.length > 0 ? (
                metrics.map((metric) => (
                  <div key={metric.name} className="flex justify-between items-center">
                    <span className="font-mono">{metric.name}</span>
                    <div className="flex gap-2 text-xs text-muted-foreground">
                      <span>avg: {metric.avg.toFixed(2)}ms</span>
                      <span>min: {metric.min}ms</span>
                      <span>max: {metric.max}ms</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-muted-foreground text-center py-4">
                  No metrics recorded yet
                </div>
              )}
            </div>
          )}

          {selectedTab === 'logs' && (
            <div className="space-y-2">
              <div className="text-muted-foreground text-center py-4">
                Log stream coming soon
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  logger.info('inspector', 'Test log from inspector', { timestamp: new Date().toISOString() });
                }}
                className="w-full"
              >
                Test Logger
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
